import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import * as XLSX from 'xlsx';

const { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiPackage, FiGlobe, FiShield, FiUpload, FiFileText, FiCheck, FiAlertCircle } = FiIcons;

const CATEGORIES = ['Equips', 'Cards', 'Talisman', 'Accessories', 'Costume', 'Materials'];
const CONDITIONS_1 = ['Any', 'Class Usable', 'For Selling'];

const DropsConfig = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    condition1: 'Any',
    condition2: '',
    is_limit: false,
    rate: ''
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (user) fetchItems();
  }, [user]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let query = supabase.from(TABLES.ITEMS).select('*');
      
      if (isSuperAdmin) {
        query = query.is('master_id', null);
      } else {
        query = query.or(`master_id.eq.${user.id},master_id.is.null`);
      }

      const { data } = await query.order('name', { ascending: true });
      setItems(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const reader = new FileReader();

    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        if (data.length === 0) throw new Error("The file is empty.");

        // Map and validate rows
        const itemsToInsert = data.map(row => ({
          name: row.Name || row.name || row.item || row.Item,
          category: row.Category || row.category || 'Materials',
          rate: parseFloat(row.Rate || row.rate || row.Price || row.price || 0),
          condition1: row.Condition1 || row['Condition 1'] || 'Any',
          condition2: row.Condition2 || row['Condition 2'] || '',
          is_limit: !!(row.Limit || row.is_limit || row['Is Limit']),
          master_id: null // Always null for Super Admin bulk upload
        })).filter(item => item.name && item.rate >= 0);

        if (itemsToInsert.length === 0) throw new Error("No valid items found in the file.");

        const { error } = await supabase.from(TABLES.ITEMS).insert(itemsToInsert);
        
        if (error) throw error;

        alert(`Successfully imported ${itemsToInsert.length} items to Global Catalog.`);
        fetchItems();
      } catch (err) {
        alert("Import Error: " + err.message);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };

    reader.readAsBinaryString(file);
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        category: item.category,
        condition1: item.condition1,
        condition2: item.condition2 || '',
        is_limit: item.is_limit,
        rate: item.rate
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        category: '',
        condition1: 'Any',
        condition2: '',
        is_limit: false,
        rate: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category || formData.rate === '') {
      alert('Please fill in mandatory fields.');
      return;
    }
    setLoading(true);
    
    const payload = {
      ...formData,
      rate: parseFloat(formData.rate),
      master_id: isSuperAdmin ? null : user.id
    };

    try {
      let error;
      if (editingItem) {
        const { error: err } = await supabase.from(TABLES.ITEMS).update(payload).eq('id', editingItem.id);
        error = err;
      } else {
        const { error: err } = await supabase.from(TABLES.ITEMS).insert([payload]);
        error = err;
      }

      if (!error) {
        setIsModalOpen(false);
        fetchItems();
      } else {
        throw error;
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (item.master_id === null && !isSuperAdmin) {
      alert("You cannot delete Global items. Contact an Admin.");
      return;
    }

    if (confirm('Delete this item? This will affect future drop logs.')) {
      const { error } = await supabase.from(TABLES.ITEMS).delete().eq('id', item.id);
      if (!error) fetchItems();
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            {isSuperAdmin ? <FiShield className="text-indigo-600" /> : <FiPackage className="text-blue-600" />}
            {isSuperAdmin ? 'Global Pricing Catalog' : 'Drops Catalog'}
          </h2>
          <p className="text-gray-400 font-bold mt-1">
            {isSuperAdmin ? 'Manage the master price list for the entire system.' : 'Configure your specific rates and view global standards.'}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          {isSuperAdmin && (
            <>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleExcelUpload} 
                accept=".xlsx, .xls, .csv" 
                className="hidden" 
              />
              <button 
                onClick={() => fileInputRef.current?.click()} 
                disabled={uploading}
                className="apple-button-secondary flex items-center justify-center gap-2 !py-4"
              >
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
                ) : (
                  <><FiUpload className="text-lg" /> Import Excel</>
                )}
              </button>
            </>
          )}
          <button onClick={() => openModal()} className="apple-button-primary flex items-center justify-center gap-2 shadow-xl !py-4">
            <FiPlus className="text-xl" /> {isSuperAdmin ? 'Add Global Item' : 'Add Custom Item'}
          </button>
        </div>
      </header>

      {/* Stats Summary for Super Admin */}
      {isSuperAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bento-card !py-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-sm">
              <FiFileText className="text-xl" />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Total Items</p>
              <p className="text-2xl font-black text-gray-900">{items.length}</p>
            </div>
          </div>
          <div className="bento-card !py-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center shadow-sm">
              <FiCheck className="text-xl" />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">System Verified</p>
              <p className="text-2xl font-black text-gray-900">100%</p>
            </div>
          </div>
          <div className="bento-card !py-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center shadow-sm">
              <FiGlobe className="text-xl" />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Visibility</p>
              <p className="text-2xl font-black text-gray-900">Global</p>
            </div>
          </div>
        </div>
      )}

      <div className="bento-card !p-0 overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left min-w-[800px]">
            <thead className="bg-[#f5f5f7] border-b border-gray-100">
              <tr>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Item Identity</th>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</th>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</th>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Rate (Php)</th>
                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="px-6 py-5">
                    <div className="font-black text-gray-900 text-base flex items-center gap-2">
                      {item.name}
                      {item.master_id === null && (
                        <span className="px-2 py-0.5 bg-blue-600 text-white rounded-md text-[8px] font-black uppercase tracking-widest">Global</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className={`text-[10px] font-black uppercase ${item.master_id === null ? 'text-blue-600' : 'text-gray-400'}`}>
                      {item.master_id === null ? 'System Default' : 'Custom'}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-5 font-black text-right text-lg text-emerald-600">
                    ₱{item.rate.toLocaleString()}
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="flex justify-center gap-1">
                      {(isSuperAdmin || item.master_id === user.id) && (
                        <>
                          <button onClick={() => openModal(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition-all"><FiEdit2 /></button>
                          <button onClick={() => handleDelete(item)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-all"><FiTrash2 /></button>
                        </>
                      )}
                      {item.master_id === null && !isSuperAdmin && (
                        <span className="text-[9px] font-bold text-gray-300 uppercase italic">Read Only</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length === 0 && !loading && (
          <div className="p-16 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">Catalog is empty.</div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsModalOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.9, opacity: 0, y: 30 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 30 }} className="relative w-full max-w-2xl bg-white rounded-[2rem] md:rounded-[3rem] p-6 md:p-12 shadow-2xl overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${isSuperAdmin ? 'bg-indigo-600' : 'bg-blue-600'} text-white rounded-xl flex items-center justify-center shadow-lg`}>
                    <FiPackage />
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-black text-gray-900">{editingItem ? 'Edit Item' : 'New Item'}</h3>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{isSuperAdmin ? 'Global System Pricing' : 'Local Master Pricing'}</p>
                  </div>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-2 bg-gray-100 rounded-full text-gray-400 hover:bg-gray-200 transition-all"><FiX /></button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Item Name *</label>
                  <input className="apple-input" placeholder="e.g., GTB Card" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Category *</label>
                  <select className="apple-input" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}>
                    <option value="">Select</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Default Condition</label>
                  <select className="apple-input" value={formData.condition1} onChange={e => setFormData({ ...formData, condition1: e.target.value })}>
                    {CONDITIONS_1.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Standard Rate (Php) *</label>
                  <div className="relative">
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-gray-400 text-lg">₱</span>
                    <input type="number" className="apple-input !pl-12 text-xl font-black text-emerald-600" placeholder="0.00" value={formData.rate} onChange={e => setFormData({ ...formData, rate: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="mt-10 flex flex-col md:flex-row gap-4">
                <button onClick={() => setIsModalOpen(false)} className="apple-button-secondary w-full">Cancel</button>
                <button onClick={handleSave} disabled={loading} className={`apple-button-primary w-full flex items-center justify-center gap-2 ${isSuperAdmin ? '!bg-indigo-600' : ''}`}>
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><FiSave /> {editingItem ? 'Update' : 'Create'}</>}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DropsConfig;